import type { Manual } from "@tebiki/shared";
import { MarkdownText } from "./MarkdownText.js";
import { mediaUrl } from "../lib/api.js";

const CAUTION_LABEL: Record<string, string> = { info: "ポイント", warning: "注意", danger: "警告" };

function todayStr(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}年${p(d.getMonth() + 1)}月${p(d.getDate())}日`;
}

/**
 * PRINT-ONLY rendering of a manual as a FORMAL business document (not a reflow of the on-screen
 * reader). Hidden on screen (display:none); shown only in `@media print`, where it also hides the
 * rest of the app. Typical-manual structure: document-control header table → numbered procedure
 * steps (no. badge + heading + image + 説明 + 注意/警告 boxes + 補足 + table) → 受講確認 sign-off.
 * Images are lazy so they don't load during normal reading; the caller force-loads them before print.
 */
export function PrintSheet({ manual, storeName }: { manual: Manual; storeName: string | null }) {
  const steps = manual.steps ?? [];
  const total = steps.length;
  const mins = manual.meta.estimatedTotalMinutes;
  const must = (manual.meta.tags ?? []).includes("必読");
  const locale = (manual.locale ?? "ja").toUpperCase();

  return (
    <div className="printsheet" aria-hidden="true">
      <style>{psCSS}</style>

      <header className="ps-head">
        <div className="ps-kicker">業務マニュアル / OPERATING MANUAL{must ? " · 必読" : ""}</div>
        <h1 className="ps-title jp">{manual.title}</h1>
        <table className="ps-meta">
          <tbody>
            <tr>
              <th>店舗</th><td>{storeName ?? "—"}</td>
              <th>カテゴリ</th><td>{manual.meta.category ?? "未分類"}</td>
            </tr>
            <tr>
              <th>版</th><td>v{manual.version ?? 1}</td>
              <th>手順数</th><td>{total} 手順</td>
            </tr>
            <tr>
              <th>標準所要</th><td>{mins ? `約 ${mins} 分` : "—"}</td>
              <th>言語</th><td>{locale}</td>
            </tr>
            <tr>
              <th>発行日</th><td>{todayStr()}</td>
              <th>必読</th><td>{must ? "はい" : "—"}</td>
            </tr>
          </tbody>
        </table>
        {manual.summary ? <p className="ps-summary jp">{manual.summary}</p> : null}
      </header>

      <ol className="ps-steps">
        {steps.map((s) => {
          const img = mediaUrl(s.imageUrl);
          return (
            <li className="ps-step" key={s.stepId}>
              <div className="ps-step-head">
                <span className="ps-step-no">手順 {String(s.order).padStart(2, "0")}</span>
                <h2 className="ps-step-title jp">{s.title}</h2>
              </div>
              <div className="ps-step-body" style={{ gridTemplateColumns: img ? "46mm 1fr" : "1fr" }}>
                {img ? <img className="ps-img" src={img} alt={s.title} loading="lazy" /> : null}
                <div className="ps-step-text">
                  <MarkdownText className="jp" text={s.caption} terms={s.highlights?.terms} />
                  {s.supplement ? <div className="ps-supp jp">補足: {s.supplement}</div> : null}
                  {s.cautions.map((c, ci) => (
                    <div key={ci} className={`ps-caution ps-caution--${c.level} jp`}>
                      <b>{CAUTION_LABEL[c.level] ?? "注意"}</b>　{c.text}
                    </div>
                  ))}
                  {s.table ? (
                    <table className="tbl">
                      <thead>
                        <tr>{s.table.columns.map((col) => <th key={col}>{col}</th>)}</tr>
                      </thead>
                      <tbody>
                        {s.table.rows.map((row, ri) => (
                          <tr key={ri}>{row.map((cell, cj) => <td key={cj} className="jp">{cell}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <footer className="ps-signoff">
        <div className="ps-signoff-title">受講確認</div>
        <table className="ps-sign">
          <tbody>
            <tr>
              <th>氏名</th><td></td>
              <th>確認日</th><td></td>
            </tr>
          </tbody>
        </table>
        <div className="ps-doc-footer">TEBIKI · {manual.title} · 発行 {todayStr()}</div>
      </footer>
    </div>
  );
}

const psCSS = `
.printsheet{display:none}
@media print{
  @page{ size:A4; margin:15mm 14mm }
  *{ -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important }
  html,body,.shell{ background:#fff !important }
  /* show ONLY the formal document; hide the on-screen reader + app chrome */
  .shell > *:not(.printsheet){ display:none !important }
  .printsheet{ display:block !important; color:#15171a; font-family:var(--font-jp); font-size:10.5pt; line-height:1.7 }

  .ps-head{ margin-bottom:14px }
  .ps-kicker{ font-family:var(--font-mono); font-size:8.5pt; letter-spacing:.16em; color:#666 }
  .ps-title{ font-family:var(--font-display); font-weight:700; font-size:23pt; line-height:1.12; letter-spacing:-.01em; margin:4px 0 12px }
  .ps-meta{ width:100%; border-collapse:collapse; font-size:9.5pt; table-layout:fixed }
  .ps-meta th,.ps-meta td{ border:1px solid #9a9a9a; padding:5px 9px; text-align:left; vertical-align:middle }
  .ps-meta th{ background:#eef0f2; width:13%; white-space:nowrap; font-weight:700 }
  .ps-summary{ margin:12px 0 0; padding:8px 12px; border-left:3px solid #15171a; background:#f6f7f8; font-size:10pt; line-height:1.7 }

  .ps-steps{ list-style:none; margin:6px 0 0; padding:0 }
  .ps-step{ break-inside:avoid; page-break-inside:avoid; padding:14px 0 12px; border-bottom:1px solid #ccd0d4 }
  .ps-step:first-child{ border-top:2px solid #15171a }
  .ps-step-head{ display:flex; align-items:baseline; gap:10px; margin-bottom:9px }
  .ps-step-no{ font-family:var(--font-mono); font-weight:700; font-size:9.5pt; color:#fff; background:#15171a; padding:3px 9px; border-radius:3px; white-space:nowrap }
  .ps-step-title{ font-family:var(--font-display); font-weight:700; font-size:14pt; line-height:1.2; margin:0 }
  .ps-step-body{ display:grid; gap:12px; align-items:start }
  .ps-img{ width:46mm; height:auto; max-height:62mm; border:1px solid #b6bbc0; border-radius:2px; object-fit:cover }
  .ps-step-text p{ margin:0 0 6px }
  /* official highlights → clean underline emphasis (typical printed-manual style) */
  .printsheet .hl{ white-space:normal }
  .printsheet .hl::before{ background:transparent !important; left:0; right:0; top:auto; bottom:0; height:0; border-bottom:2.4px solid #15171a; transform:none }
  .ps-caution{ border:1px solid #555; border-left:4px solid #555; background:#f8f8f8; padding:6px 11px; margin:8px 0; font-size:9.5pt; line-height:1.6 }
  .ps-caution--warning{ border-color:#9a6a00; border-left-color:#9a6a00; background:#fff8e9 }
  .ps-caution--danger{ border-color:#a3201a; border-left-color:#a3201a; background:#fcf0ef }
  .ps-caution b{ font-weight:700 }
  .ps-supp{ font-size:9.5pt; color:#444; margin:7px 0; padding-left:11px; border-left:2px solid #c7ccd1 }
  .printsheet .tbl{ width:100%; border-collapse:collapse; font-size:9.5pt; margin:9px 0 }
  .printsheet .tbl th,.printsheet .tbl td{ border:1px solid #aab0b5; padding:5px 8px; text-align:left }
  .printsheet .tbl th{ background:#eef0f2; font-weight:700 }

  .ps-signoff{ break-inside:avoid; margin-top:18px; padding-top:10px; border-top:2px solid #15171a }
  .ps-signoff-title{ font-family:var(--font-mono); font-size:9pt; letter-spacing:.16em; color:#333; margin-bottom:7px }
  .ps-sign{ width:100%; border-collapse:collapse; font-size:9.5pt }
  .ps-sign th,.ps-sign td{ border:1px solid #9a9a9a; padding:11px 9px; text-align:left }
  .ps-sign th{ background:#eef0f2; width:13%; white-space:nowrap; font-weight:700 }
  .ps-doc-footer{ margin-top:12px; font-family:var(--font-mono); font-size:8pt; color:#888; text-align:center }
}
`;
